import { ChevronUp, ChevronDown, User, ExternalLink, Figma, FileCode, MessageSquare, BookOpen } from "lucide-react";
import { useState } from "react";

import { PersonaIcon } from "@/assets/svg-icons/persona-icon";
import {
  CitationCardProps,
  ContentCardProps,
  ContributorCardProps,
  PersonaCardProps,
  ScenarioCardProps,
  SectionHeaderProps,
  SectionProps,
} from "@/types/unified-model";
import { NodeCitation } from "@/service-hooks/use-get-functional-graph-service";
import { getFileTypeIconByFileName } from "@/lib/utils";
import { useTheme } from "@/context/theme-context";
import CustomTooltip from "@/components/elements/custom-tooltip";

// Define TypeScript interfaces for our data structure
export const Section: React.FC<SectionProps> = ({
  title,
  children,
  headerSize = "text-base",
}) => {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className="mb-3">
      <div
        className={`bg-surface-active flex flex-col  ${
          expanded ? "rounded-t-md" : "rounded-md"
        } p-3 `}
      >
        <div
          className="flex justify-between items-center cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <span className={`${headerSize} text-white`}>{title}</span>
          <ChevronUp
            size={16}
            className={`transition-transform text-white ${
              expanded ? "rotate-0" : "rotate-180"
            }`}
          />
        </div>
      </div>
      {expanded && (
        <div className="p-3 bg-surface-hover rounded-b-md ">{children}</div>
      )}
    </div>
  );
};

export const PersonaCard: React.FC<PersonaCardProps> = ({
  title,
  subtitle,
  onClick,
  isActive,
  headerSize = "text-sm",
  contentSize = "text-xs",
  icon = true,
}) => {
  const { theme } = useTheme();
  const isLightTheme = theme === 'light';

  return (
    <button
      className={`${
        isActive ? "bg-primary" : "bg-surface"
      } p-2 rounded-md flex items-center text-sm gap-2 text-left w-full ${
        isLightTheme ? `border ${isActive ? "border-primary" : "border-surface-border"}` : ""
      }`}
      onClick={onClick}
    >
    {icon && (
      <div
        className={`w-8 h-8 ${
          isActive ? "bg-primary-dark" : "bg-surface-hover"
        } rounded-full flex items-center justify-center flex-shrink-0`}
      >
        <PersonaIcon className={isActive ? "text-button-text" : "text-text-primary"} />
      </div>
    )}
    <div className="flex flex-col min-w-0 flex-1">
      <span
        className={`${headerSize} line-clamp-2 break-all ${isActive ? "text-button-text" : "text-text-primary"}`}
        title={title}
      >
        {title}
      </span>
      <span className={`text-muted ${contentSize} line-clamp-1 break-all`}>
        {subtitle}
      </span>
    </div>
  </button>
  );
};
export const CitationCard: React.FC<CitationCardProps> = ({
  title='NA',
  onClick,
  contentSize = "text-xs",
  citationType,
}) => {
  // Helper function to check if filename has an extension
  const hasFileExtension = (filename: string): boolean => {
    if (!filename || filename === 'NA' || filename === 'Text Chunk') return false;
    const lastDot = filename.lastIndexOf('.');
    const lastSlash = Math.max(filename.lastIndexOf('/'), filename.lastIndexOf('\\'));
    return lastDot > lastSlash && lastDot > 0 && lastDot < filename.length - 1;
  };

  const isNA = title === 'NA';
  const isTextChunk = title === "Text Chunk";
  const isUser = citationType === 'user';
  const isJira = citationType === 'jira';
  const isFigma = citationType === 'figma';
  const isConfluence = citationType === 'confluence';
  const isCode = citationType === 'code';
  const isPrompt = citationType === 'prompt';
  const isDocument = citationType === 'document' || !citationType;

  // code and prompt types are display-only (not clickable)
  const isDisplayOnly = isCode || isPrompt;

  const hasNoExtension = isDocument && !hasFileExtension(title);
  const isDisabled = isNA || isTextChunk || isUser || isDisplayOnly || (isDocument && hasNoExtension);

  // Generate tooltip message based on reason for being disabled
  const getTooltipMessage = (): string => {
    if (isNA) return "No source available";
    if (isTextChunk) return "Text chunk - no file to open";
    if (isUser) return "User citation";
    if (isCode) return "Code reference";
    if (isPrompt) return "Prompt reference";
    if (hasNoExtension) return "No file available";
    return "";
  };

  // Get type badge color and label
  const getTypeBadge = () => {
    if (isJira) return { color: "bg-blue-500/20 text-blue-400", label: "Jira" };
    if (isUser) return { color: "bg-green-500/20 text-green-400", label: "User" };
    if (isFigma) return { color: "bg-pink-500/20 text-pink-400", label: "Figma" };
    if (isConfluence) return { color: "bg-cyan-500/20 text-cyan-400", label: "Confluence" };
    if (isCode) return { color: "bg-orange-500/20 text-orange-400", label: "Code" };
    if (isPrompt) return { color: "bg-yellow-500/20 text-yellow-400", label: "Prompt" };
    return { color: "bg-purple-500/20 text-purple-400", label: "Document" };
  };

  const badge = getTypeBadge();

  const renderIcon = () => {
    if (isUser) {
      return (
        <div className="w-6 h-6 bg-surface-hover rounded flex items-center justify-center text-green-400 flex-shrink-0">
          <User className="w-4 h-4" />
        </div>
      );
    }
    if (isJira) {
      return (
        <div className="w-6 h-6 bg-surface-hover rounded flex items-center justify-center text-blue-400 flex-shrink-0">
          <ExternalLink className="w-4 h-4" />
        </div>
      );
    }
    if (isFigma) {
      return (
        <div className="w-6 h-6 bg-surface-hover rounded flex items-center justify-center text-pink-400 flex-shrink-0">
          <Figma className="w-4 h-4" />
        </div>
      );
    }
    if (isConfluence) {
      return (
        <div className="w-6 h-6 bg-surface-hover rounded flex items-center justify-center text-cyan-400 flex-shrink-0">
          <BookOpen className="w-4 h-4" />
        </div>
      );
    }
    if (isCode) {
      return (
        <div className="w-6 h-6 bg-surface-hover rounded flex items-center justify-center text-orange-400 flex-shrink-0">
          <FileCode className="w-4 h-4" />
        </div>
      );
    }
    if (isPrompt) {
      return (
        <div className="w-6 h-6 bg-surface-hover rounded flex items-center justify-center text-yellow-400 flex-shrink-0">
          <MessageSquare className="w-4 h-4" />
        </div>
      );
    }
    // document type - existing behavior
    if (title !== 'NA') {
      return (
        <div className="w-6 h-6 bg-surface-hover rounded flex items-center justify-center text-white flex-shrink-0">
          <img
            loading="lazy"
            src={getFileTypeIconByFileName(title)}
            alt={title}
            className="object-contain"
          />
        </div>
      );
    }
    return null;
  };

  const buttonContent = (
    <button
      className={`bg-surface px-2 py-1.5 rounded-md flex items-center text-sm gap-1.5 text-left disabled:opacity-40 disabled:cursor-not-allowed w-full`}
      onClick={onClick}
      disabled={isDisabled}
    >
      {renderIcon()}
      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        <span
          className={`${contentSize} line-clamp-2 break-all text-text-primary`}
          title={title}
        >
          {title}
        </span>
        {citationType && (
          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded w-fit ${badge.color}`}>
            {badge.label}
          </span>
        )}
      </div>
    </button>
  );

  // Wrap in tooltip if disabled
  if (isDisabled) {
    return (
      <CustomTooltip text={getTooltipMessage()} side="top">
        {buttonContent}
      </CustomTooltip>
    );
  }

  return buttonContent;
};

interface CitationListProps {
  citations: NodeCitation[];
  onCitationClick: (citation: NodeCitation) => void;
  initialVisible?: number;
  contentSize?: string;
}

export const CitationList: React.FC<CitationListProps> = ({
  citations,
  onCitationClick,
  initialVisible = 5,
  contentSize = "text-xs",
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const total = citations.length;
  const hasMore = total > initialVisible;
  const visibleCitations = isExpanded || !hasMore ? citations : citations.slice(0, initialVisible);
  const hiddenCount = total - initialVisible;

  return (
    <>
      <div className="grid grid-cols-1 gap-1.5">
        {visibleCitations.map((citation, idx) => (
          <CitationCard
            key={`${citation.type}-${citation._id}-${idx}`}
            title={citation.name}
            citationType={citation.type}
            onClick={() => onCitationClick(citation)}
            contentSize={contentSize}
          />
        ))}
      </div>
      {hasMore && (
        <button
          type="button"
          onClick={() => setIsExpanded((v) => !v)}
          className="mt-2 flex items-center gap-1 text-xs text-muted hover:text-white transition-colors"
        >
          {isExpanded ? (
            <>
              <span>Show less</span>
              <ChevronUp className="h-3 w-3" />
            </>
          ) : (
            <>
              <span>Show {hiddenCount} more</span>
              <ChevronDown className="h-3 w-3" />
            </>
          )}
        </button>
      )}
    </>
  );
};

// ScenarioCard component
export const ScenarioCard: React.FC<ScenarioCardProps> = ({ scenario }) => (
  <div className="bg-surface rounded p-3 mb-2">
    <h4 className="font-medium mb-2">{scenario.scenario}</h4>
    <p className="text-sm mb-2">{scenario.description}</p>

    {scenario.steps.map((step, index) => (
      <div key={index} className="mb-3">
        <h5 className="text-sm font-medium mb-1">{step.step}</h5>

        <div className="pl-3 border-l-2 border-surface-border">
          {step.actions.map((action, actionIdx) => (
            <div key={actionIdx} className="mb-2">
              <p className="text-xs">{action.action}</p>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

// ContributorCard component
export const ContributorCard: React.FC<ContributorCardProps> = ({
  name,
  image,
}) => (
  <div className="bg-surface p-2 rounded-md flex flex-col items-center text-sm">
    <img
      loading="lazy"
      src={image}
      alt={name}
      className="w-12 h-12 rounded-full mb-2"
    />
    <span className="text-sm text-center">{name} </span>
  </div>
);

export const ContentDiv: React.FC<ContentCardProps> = ({
  content,
  isActive = false,
  contentSize = "text-xs",
}) => (
  <div
    className={`px-2 py-1.5 ${contentSize} rounded-md text-left ${
      isActive ? "bg-primary text-button-text" : "bg-surface text-text-primary"
    }`}
  >
    {content}
  </div>
);

export const ContentButton: React.FC<ContentCardProps> = ({
  content,
  onClick,
  isActive,
  contentSize = "text-xs",
}) => (
  <button
    onClick={onClick}
    className={`px-2 py-1.5 ${contentSize} rounded-md text-left ${
      isActive ? "bg-primary text-button-text" : "bg-surface text-text-primary"
    }`}
  >
    {content}
  </button>
);

export const SectionHeader = <T extends object>({
  onEdit,
  editKey,
  editData,
}: SectionHeaderProps<T>) => (
  <div className="flex items-center justify-between">
    <div className="flex justify-end items-center mb-2">
      <button
        onClick={() => onEdit(editKey, editData)}
        className="bg-primary px-[0.375rem] py-[0.125rem] rounded text-sm text-white"
      >
        Modify
      </button>
    </div>
  </div>
);
